import type {
  EncryptionSessionCache,
  EncryptionSessionCacheEntry,
  SDKArguments,
} from "@seald-io/sdk/lib/main";

const IDB_VERSION = 1;
const STORE = "sessions";

function openDb(name: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(name, IDB_VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error("IDB open blocked"));
  });
}

function idbGet(
  db: IDBDatabase,
  id: string,
): Promise<EncryptionSessionCacheEntry | undefined> {
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE, "readonly").objectStore(STORE).get(id);
    req.onsuccess = () =>
      resolve(req.result as EncryptionSessionCacheEntry | undefined);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(
  db: IDBDatabase,
  id: string,
  value: EncryptionSessionCacheEntry,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(value, id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function idbDelete(db: IDBDatabase, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function idbAllKeys(db: IDBDatabase): Promise<string[]> {
  return new Promise((resolve, reject) => {
    const req = db
      .transaction(STORE, "readonly")
      .objectStore(STORE)
      .getAllKeys();
    req.onsuccess = () => resolve(req.result as string[]);
    req.onerror = () => reject(req.error);
  });
}

// Fire-and-forget sweep run on DB open and after each set.
function purgeExpired(db: IDBDatabase, ttlMs: number): void {
  const now = Date.now();
  const tx = db.transaction(STORE, "readwrite");
  const req = tx.objectStore(STORE).openCursor();
  req.onsuccess = () => {
    const cursor = req.result;
    if (!cursor) return;
    if (
      now - (cursor.value as EncryptionSessionCacheEntry).serializationDate >
      ttlMs
    ) {
      cursor.delete();
    }
    cursor.continue();
  };
}

/**
 * IndexedDB-backed Seald encryption session cache.
 *
 * Drop-in replacement for `createLocalStorageEncryptionSessionCache`. Avoids
 * localStorage quota limits — IDB quota is browser-managed (typically 50MB+),
 * so users who decrypt many sessions no longer hit QuotaExceededError.
 *
 * Async get/set are natively supported by the SDK's EncryptionSessionCache
 * interface, so no API compromise is required.
 */
export const createIndexedDBEncryptionSessionCache: NonNullable<
  SDKArguments["createEncryptionSessionCache"]
> = async ({ appId, databasePath, encryptionSessionCacheTTL }) => {
  const dbName = ["seald-idb", appId, databasePath].filter(Boolean).join("-");
  const db = await openDb(dbName);

  if (encryptionSessionCacheTTL > 0) {
    purgeExpired(db, encryptionSessionCacheTTL);
  }

  const cache: EncryptionSessionCache = {
    async delete(id) {
      await idbDelete(db, id);
    },

    async get(id, dateOnly) {
      const entry = await idbGet(db, id);
      if (!entry) return null;

      if (
        encryptionSessionCacheTTL > 0 &&
        Date.now() - entry.serializationDate > encryptionSessionCacheTTL
      ) {
        void idbDelete(db, id);
        return null;
      }

      // dateOnly is an optional optimization hint: caller only needs the date
      // to decide whether to evict, not the full sym key.
      if (dateOnly) {
        return {
          ...entry,
          sessionSymKey: null as unknown as string,
        };
      }

      return entry;
    },

    async keys() {
      return idbAllKeys(db);
    },

    async set(id, value) {
      await idbPut(db, id, value);
    },
  };

  return cache;
};
